import React, { ReactNode } from 'react';
import styled from 'styled-components';

export type NoteType = 'operational' | 'risk' | 'milestone' | 'general';
export type ImpactLevel = 'no-impact' | 'minor-risk' | 'delay-expected';
export type Visibility = 'internal' | 'customer-visible';

export interface Note {
  id: string;
  createdAt: string;
  author: string;
  noteType: NoteType;
  content: string;
  impactLevel: ImpactLevel;
  visibility: Visibility;
  riskCause?: string;
  delay?: string;
  delayCustom?: string;
  affectsStage?: string;
  nextAction?: string;
  nextActionDueBy?: string;
  attachments?: File[];
  notified?: {
    internal?: boolean;
    customer?: boolean;
    ops?: boolean;
  };
}

export interface NotesProps {
  notes: Note[];
  isLoading?: boolean;
  title?: string;
  emptyMessage?: string;
  actions?: ReactNode;
}

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const NotesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NotesTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const NotesCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.regular};
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const NoteCard = styled.div<{ $visibility: Visibility }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border-left: 3px solid ${({ theme, $visibility }) => 
    $visibility === 'customer-visible' ? theme.colors.primary : theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all 0.2s;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const NoteMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

const NoteTypeBadge = styled.span<{ $type: NoteType }>`
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  background: ${({ theme, $type }) => {
    const colors = {
      operational: theme.colors.primary,
      risk: '#ef4444',
      milestone: '#16a34a',
      general: theme.colors.gray400,
    };
    return `${colors[$type]}15`;
  }};
  color: ${({ theme, $type }) => {
    const colors = {
      operational: theme.colors.primary,
      risk: '#ef4444',
      milestone: '#16a34a',
      general: theme.colors.gray700,
    };
    return colors[$type];
  }};
`;

const ImpactBadge = styled.span<{ $impact: ImpactLevel }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: ${({ $impact }) => {
    const colors = {
      'no-impact': '#16a34a',
      'minor-risk': '#eab308',
      'delay-expected': '#ef4444',
    };
    return `${colors[$impact]}15`;
  }};
  color: ${({ $impact }) => {
    const colors = {
      'no-impact': '#16a34a',
      'minor-risk': '#eab308',
      'delay-expected': '#ef4444',
    };
    return colors[$impact];
  }};
`;

const VisibilityBadge = styled.span<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: ${({ theme, $visible }) => $visible ? `${theme.colors.primary}15` : `${theme.colors.gray300}15`};
  color: ${({ theme, $visible }) => $visible ? theme.colors.primary : theme.colors.gray600};
`;

const NoteTimestamp = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const NoteAuthor = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const NoteContent = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const NoteDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  padding-top: ${({ theme }) => theme.spacing[2]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const DetailLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const DetailValue = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const AttachmentChip = styled.span`
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const impactIcons = {
  'no-impact': '✓',
  'minor-risk': '⚠',
  'delay-expected': '⏱',
};

const impactLabels = {
  'no-impact': 'No impact',
  'minor-risk': 'Minor risk',
  'delay-expected': 'Delay expected',
};

const noteTypeLabels = {
  operational: 'Operational',
  risk: 'Risk/Issue',
  milestone: 'Milestone',
  general: 'General',
};

export const Notes: React.FC<NotesProps> = ({ 
  notes, 
  isLoading = false,
  title = 'Notes',
  emptyMessage = 'No notes yet. Add a note to track changes, risks, and milestones.',
  actions,
}) => {
  if (isLoading) {
    return (
      <NotesContainer>
        <NotesHeader>
          <NotesTitle>{title}</NotesTitle>
          {actions}
        </NotesHeader>
        <EmptyState>Loading notes...</EmptyState>
      </NotesContainer>
    );
  }

  if (notes.length === 0) {
    return (
      <NotesContainer>
        <NotesHeader>
          <NotesTitle>{title}</NotesTitle>
          {actions}
        </NotesHeader>
        <EmptyState>{emptyMessage}</EmptyState>
      </NotesContainer>
    );
  }

  return (
    <NotesContainer>
      <NotesHeader>
        <NotesTitle>
          {title} <NotesCount>({notes.length})</NotesCount>
        </NotesTitle>
        {actions}
      </NotesHeader>

      <NotesList>
        {notes.map((note) => (
          <NoteCard key={note.id} $visibility={note.visibility}>
            <NoteHeader>
              <NoteMetadata>
                <NoteTypeBadge $type={note.noteType}>{noteTypeLabels[note.noteType]}</NoteTypeBadge>
                <ImpactBadge $impact={note.impactLevel}>
                  <span>{impactIcons[note.impactLevel]}</span>
                  <span>{impactLabels[note.impactLevel]}</span>
                </ImpactBadge>
                <VisibilityBadge $visible={note.visibility === 'customer-visible'}>
                  {note.visibility === 'customer-visible' ? '👁 Customer visible' : '🔒 Internal only'}
                </VisibilityBadge>
              </NoteMetadata>
              <NoteTimestamp>{new Date(note.createdAt).toLocaleString()}</NoteTimestamp>
            </NoteHeader>

            <div>
              <NoteAuthor>{note.author}</NoteAuthor>
              <NoteContent>{note.content}</NoteContent>
            </div>

            {(note.riskCause || note.delay !== 'none' || note.nextAction || (note.attachments && note.attachments.length > 0) || note.notified) && (
              <NoteDetails>
                {note.riskCause && (
                  <DetailRow>
                    <DetailLabel>Root cause:</DetailLabel>
                    <DetailValue>{note.riskCause.replace('-', ' ')}</DetailValue>
                  </DetailRow>
                )}

                {note.delay !== 'none' && note.delay && (
                  <DetailRow>
                    <DetailLabel>Expected delay:</DetailLabel>
                    <DetailValue>
                      {note.delay === 'custom' ? note.delayCustom : note.delay}
                      {note.affectsStage && ` → affects ${note.affectsStage}`}
                    </DetailValue>
                  </DetailRow>
                )}

                {note.nextAction && (
                  <DetailRow>
                    <DetailLabel>Next action:</DetailLabel>
                    <DetailValue>
                      {note.nextAction}
                      {note.nextActionDueBy && ` (due: ${new Date(note.nextActionDueBy).toLocaleString()})`}
                    </DetailValue>
                  </DetailRow>
                )}

                {note.notified && (
                  <DetailRow>
                    <DetailLabel>Notified:</DetailLabel>
                    <DetailValue>
                      {[
                        note.notified.internal && 'Internal team',
                        note.notified.customer && 'Customer',
                        note.notified.ops && 'Operations',
                      ]
                        .filter(Boolean)
                        .join(', ') || 'None'}
                    </DetailValue>
                  </DetailRow>
                )}

                {note.attachments && note.attachments.length > 0 && (
                  <DetailRow>
                    <DetailLabel>Attachments:</DetailLabel>
                    <AttachmentsList>
                      {note.attachments.map((file, idx) => (
                        <AttachmentChip key={idx}>📎 {file.name}</AttachmentChip>
                      ))}
                    </AttachmentsList>
                  </DetailRow>
                )}
              </NoteDetails>
            )}
          </NoteCard>
        ))}
      </NotesList>
    </NotesContainer>
  );
};
